import type { AlertProps as LegacyAlertProps } from './Alert';
import type { AlertRole, AlertSeverity } from '@natter-co/design-system';

/**
 * Maps the legacy MUI `Alert` props onto the Design System 2.0 API.
 *
 * TODO(HNS-264): temporary. This mapper exists only so the visual refresh can be gated inside the
 * wrapper instead of at ~90 call sites; retiring the flag means deleting it and letting call sites
 * speak the DS vocabulary directly.
 *
 * Returning `null` means "the DS alert cannot express this one" and sends the caller back to MUI.
 * That is deliberate: a silent best-effort mapping would ship alerts that look or behave subtly
 * wrong with nothing to catch it at review time. Every `null` below is a design question that has
 * to be answered before those call sites can be refreshed.
 */
type DsProps = { severity: AlertSeverity; title?: LegacyAlertProps['title']; role?: AlertRole };

/**
 * Mapped by CELL, not by name.
 *
 * `white` is the wrapper's default and therefore what most of today's call sites render. DS 2.0
 * draws that cell as `neutral` — the page colour bounded by a border, with the icon in the text
 * colour — which `components/Alert/types.ts` calls out as the closest counterpart to `white`.
 *
 * `infoGray` and `infoDark` are theme-local severities (`MuiAlert.tsx` variants) with no DS cell of
 * their own. They go to `neutral` too, by design decision (2026-09-24): neutral is already the grey
 * surface one step off the page, which is what `infoGray` draws, and both are hue-less notices.
 * `infoDark` has a single call site, on the Open Natter waiting panel.
 *
 * `transparent` has no call sites and no DS counterpart, so it stays on MUI.
 */
const SEVERITY: Partial<Record<NonNullable<LegacyAlertProps['variant']>, AlertSeverity>> = {
  white: 'neutral',
  infoGray: 'neutral',
  infoDark: 'neutral',
  info: 'info',
  success: 'success',
  warning: 'warning',
  error: 'error',
};

/** The only two the DS alert announces with; anything else is not a politeness it can honour. */
const isDsRole = (role: string): role is AlertRole => role === 'alert' || role === 'status';

/**
 * Cases the DS alert cannot express, all of which stay on MUI:
 *
 *   - `Icon` — a caller-supplied MUI glyph, or `false` for none. The DS alert owns its icon per
 *     severity, and the shape is load-bearing there: it is what makes severity legible without
 *     colour vision.
 *   - a bodyless alert — DS requires `children`, because a title with no body is a heading and the
 *     set draws no such cell.
 *   - a `role` other than `alert`/`status`.
 *   - `dismissible` TOGETHER WITH `onClose` — see below.
 *
 * Two props that used to be refused and no longer are, because the wrapper now handles them:
 *
 *   - `sx` — the wrapper puts the DS alert inside a MUI `Box` carrying it. Every call site's `sx` is
 *     outer spacing or width, which the box honours and the full-width panel fills. An `sx` that
 *     styled the panel ITSELF (a background, a border) would be lost, so do not add one.
 *   - `actions` — the DS alert has an `actions` slot, and the wrapper renders the same buttons and
 *     links into it. `actionsGap` is MUI-only; the DS row has its own fixed gap.
 *
 * `dismissible` on its own is NOT refused. DS 2.0 expresses dismissal as `onDismiss` +
 * `dismissLabel` rather than as a boolean, and leaves the hiding to the caller. The wrapper IS that
 * caller: the `alert-<id>` localStorage memory and the self-hide were always its own, on the MUI
 * path too, so the refreshed path keeps the same behaviour and changes only the appearance.
 *
 * `onClose` alongside it is the part that cannot be honoured. DS's `onDismiss` takes no argument
 * and MUI's `onClose` is typed to receive the event that caused the close, so satisfying it would
 * mean fabricating an event or dropping the callback without saying so. No call site pairs them
 * today — the pairing exists only in this component's own tests — so refusing costs nothing and
 * keeps the failure visible if one ever does.
 */
export const toDsProps = ({
  variant = 'white',
  title,
  description,
  children,
  Icon,
  dismissible,
  onClose,
  role,
}: LegacyAlertProps): DsProps | null => {
  if (Icon !== undefined) {
    return null;
  }

  if (dismissible && onClose) {
    return null;
  }

  // DS `children` is required, and it is the description slot.
  if (description === undefined && children === undefined) {
    return null;
  }

  if (role !== undefined && !isDsRole(role)) {
    return null;
  }

  const severity = SEVERITY[variant];

  if (!severity) {
    return null;
  }

  return { severity, title, role };
};
